import { useMemo } from "react";
import { buildTableDetectionSource } from "pages/grid/utils/gridHelpers";

export function useGridTableCapabilities({
  tableTitle,
  layoutPageTitle,
  tableWidgetName,
  tableWidgetFields,
  selectedDataSource,
  isDashboardLayout,
  isTutorialsLayout,
  isAdminTutorialsLayout,
}: {
  tableTitle: string;
  layoutPageTitle?: string;
  tableWidgetName: unknown;
  tableWidgetFields?: Array<{ field?: string }>;
  selectedDataSource: any;
  isDashboardLayout: boolean;
  isTutorialsLayout: boolean;
  isAdminTutorialsLayout: boolean;
}) {
  const tableDetectionSource = useMemo(() => {
    return buildTableDetectionSource(tableTitle, tableWidgetName, selectedDataSource);
  }, [tableTitle, tableWidgetName, selectedDataSource]);

  const isTvaTable = useMemo(() => /t\s*v\s*a|vat/.test(tableDetectionSource), [tableDetectionSource]);

  const isEnergyTable = useMemo(
    () => /energy|energies/.test(tableDetectionSource) && !/yearloss/.test(tableDetectionSource),
    [tableDetectionSource]
  );
  const isEnergySuperTable = useMemo(() => {
    const hasMgtPattern =
      tableDetectionSource.includes("energy_mgt") ||
      tableDetectionSource.includes("energy-mgt") ||
      tableDetectionSource.includes("energy mgt") ||
      tableDetectionSource.includes("energies_mgt") ||
      tableDetectionSource.includes("energies-mgt") ||
      tableDetectionSource.includes("energies mgt");
    const hasManagementNaming =
      tableDetectionSource.includes("gestion des energie") ||
      tableDetectionSource.includes("gestion des energies") ||
      tableDetectionSource.includes("energie management") ||
      tableDetectionSource.includes("energies management");

    return hasMgtPattern || hasManagementNaming;
  }, [tableDetectionSource]);

  const isLocaleTable = useMemo(() => /locale/.test(tableDetectionSource), [tableDetectionSource]);
  const isEnvironmentTable = useMemo(
    () => /(^|[^a-z])(environnement|environment)([^a-z]|$)/.test(tableDetectionSource),
    [tableDetectionSource]
  );
  const isAdminUsersTable = useMemo(
    () => {
      const hasNameMatch = /(^|[^a-z])(adminuser|admin users|droits admin)([^a-z]|$)/.test(tableDetectionSource);
      const fieldNames = new Set((tableWidgetFields ?? []).map((f) => String(f?.field ?? "").toLowerCase()));
      const hasColumnsMatch =
        fieldNames.has("username") &&
        fieldNames.has("email") &&
        fieldNames.has("entities") &&
        fieldNames.has("environnement_mgt");

      return hasNameMatch || hasColumnsMatch;
    },
    [tableDetectionSource, tableWidgetFields]
  );
  const isAccessoryGroupTable = useMemo(() => /accessorygroup/.test(tableDetectionSource), [tableDetectionSource]);
  const isYearLossTable = useMemo(
    () => /yearloss|d[ée]perdition/.test(tableDetectionSource),
    [tableDetectionSource]
  );
  const isAccessorySuperTable = useMemo(
    () => /accessory[_\s-]*mgt|accessorymgt/.test(tableDetectionSource),
    [tableDetectionSource]
  );
  const isAccessoryEditTable = useMemo(
    () =>
      (/accessoires/.test(tableDetectionSource) || /(^|[^a-z])accessory(?!group|mgt)([^a-z]|$)/.test(tableDetectionSource)) &&
      !/accessorygroup/.test(tableDetectionSource) &&
      !isAccessorySuperTable,
    [isAccessorySuperTable, tableDetectionSource]
  );
  const isHpSuperTable = useMemo(
    () => /pompes[_\s-]*mgt|heatpump[_\s-]*mgt|heatpumpmgt/.test(tableDetectionSource),
    [tableDetectionSource]
  );
  const isHpTable = useMemo(
    () => /pompes|heatpumps?/.test(tableDetectionSource) && !/heatpumpdiscount/.test(tableDetectionSource) && !isHpSuperTable,
    [isHpSuperTable, tableDetectionSource]
  );
  const isAnnexTable = useMemo(() => /annex/.test(tableDetectionSource), [tableDetectionSource]);
  const isFaqTable = useMemo(() => {
    const hasNameMatch = /(^|[^a-z])faq([^a-z]|$)|help\/?faq/.test(tableDetectionSource);
    const fieldNames = new Set((tableWidgetFields ?? []).map((f) => String(f?.field ?? "").toLowerCase()));
    const hasColumnsMatch =
      fieldNames.has("question_fr") &&
      fieldNames.has("question_en") &&
      fieldNames.has("answer_fr") &&
      fieldNames.has("answer_en");
    return hasNameMatch || hasColumnsMatch;
  }, [tableDetectionSource, tableWidgetFields]);
  const isVersionsTable = useMemo(() => {
    const hasNameMatch = /(^|[^a-z])versions?([^a-z]|$)|[eé]volution\s*pjp4|gestion\s*[eé]volution/.test(tableDetectionSource);
    const fieldNames = new Set((tableWidgetFields ?? []).map((f) => String(f?.field ?? "").toLowerCase()));
    const hasColumnsMatch =
      fieldNames.has("version") &&
      fieldNames.has("fr") &&
      fieldNames.has("en");
    return hasNameMatch || hasColumnsMatch;
  }, [tableDetectionSource, tableWidgetFields]);
  const isTraceTable = useMemo(() => {
    const source = `${tableDetectionSource} ${(layoutPageTitle || "").toLowerCase()}`;
    return /(^|[^a-z])(trace|traces|statistiques\s*projets?|project\s*stats?)([^a-z]|$)/.test(source);
  }, [tableDetectionSource, layoutPageTitle]);
  const isSatisfactionTable = useMemo(() => {
    const hasNameMatch = /(^|[^a-z])(satisfaction|questionnaire)([^a-z]|$)/.test(tableDetectionSource);
    const fieldNames = new Set((tableWidgetFields ?? []).map((f) => String(f?.field ?? "").toLowerCase()));
    const hasColumnsMatch =
      fieldNames.has("satisfied") &&
      fieldNames.has("environnement_id");
    return hasNameMatch || hasColumnsMatch;
  }, [tableDetectionSource, tableWidgetFields]);
  const isParameterTable = useMemo(() => /parameter/.test(tableDetectionSource), [tableDetectionSource]);
  const isDiscountManagementTable = useMemo(
    () => /discount\s*management|heatpumpdiscount/.test(tableDetectionSource),
    [tableDetectionSource]
  );
  const isMenuDefsTable = useMemo(() => {
    const baseUrl = String(selectedDataSource?.baseUrl ?? "").toLowerCase();
    const source = `${tableDetectionSource} ${String(tableTitle || "").toLowerCase()}`;
    return baseUrl.includes("/menudefs") || /(^|[^a-z])menudefs?([^a-z]|$)/.test(source);
  }, [selectedDataSource, tableDetectionSource, tableTitle]);

  const canCreate = useMemo(() => {
    if (isDashboardLayout) {
      return Boolean(
        isMenuDefsTable &&
          selectedDataSource?.create?.active
      );
    }
    return Boolean(selectedDataSource?.create?.active);
  }, [isDashboardLayout, isMenuDefsTable, selectedDataSource]);

  const canEdit = useMemo(() => {
    if (isDashboardLayout) {
      return isMenuDefsTable;
    }
    if (isTutorialsLayout && !isAdminTutorialsLayout) return false;
    return Boolean(selectedDataSource?.update?.active && selectedDataSource?.update?.endpoint);
  }, [isDashboardLayout, isTutorialsLayout, isAdminTutorialsLayout, isMenuDefsTable, selectedDataSource]);

  const canDelete = useMemo(() => {
    if (isDashboardLayout) {
      return Boolean(
        isMenuDefsTable &&
          selectedDataSource?.trash?.active &&
          selectedDataSource?.trash?.endpoint
      );
    }
    if (isTutorialsLayout && !isAdminTutorialsLayout) return false;
    return Boolean(selectedDataSource?.trash?.active && selectedDataSource?.trash?.endpoint);
  }, [
    isDashboardLayout,
    isTutorialsLayout,
    isAdminTutorialsLayout,
    isMenuDefsTable,
    selectedDataSource,
  ]);

  return {
    isTvaTable,
    isEnergyTable,
    isEnergySuperTable,
    isLocaleTable,
    isEnvironmentTable,
    isAdminUsersTable,
    isAccessoryGroupTable,
    isYearLossTable,
    isAccessorySuperTable,
    isAccessoryEditTable,
    isHpSuperTable,
    isHpTable,
    isAnnexTable,
    isFaqTable,
    isVersionsTable,
    isTraceTable,
    isSatisfactionTable,
    isParameterTable,
    isDiscountManagementTable,
    isMenuDefsTable,
    canCreate,
    canEdit,
    canDelete,
    canOpenTvaCreate: isTvaTable,
    canOpenTvaEdit: isTvaTable,
    canOpenEnergyCreate: isEnergySuperTable,
    canOpenEnergyEdit: isEnergyTable,
    canOpenLocaleCreate: isLocaleTable,
    canOpenLocaleEdit: isLocaleTable,
    canOpenEnvironmentCreate: isEnvironmentTable,
    canOpenEnvironmentEdit: isEnvironmentTable,
    canOpenAdminUsersCreate: isAdminUsersTable,
    canOpenAdminUsersEdit: isAdminUsersTable,
    canOpenAccessoryGroupCreate: isAccessoryGroupTable && canCreate,
    canOpenAccessoryGroupEdit: isAccessoryGroupTable,
    canOpenAccessoryEdit: isAccessoryEditTable,
    canOpenAccessorySuperCreate: isAccessorySuperTable,
    canOpenAccessorySuperEdit: isAccessorySuperTable,
    canOpenHpSuperCreate: isHpSuperTable,
    canOpenHpSuperEdit: isHpSuperTable,
    canOpenHpEdit: isHpTable,
    canOpenYearLossEdit: isYearLossTable,
    canOpenAnnexCreate: isAnnexTable,
    canOpenAnnexEdit: isAnnexTable,
    canOpenFaqCreate: isFaqTable,
    canOpenFaqEdit: isFaqTable,
    canOpenVersionCreate: isVersionsTable,
    canOpenVersionEdit: isVersionsTable,
    canOpenTraceView: isTraceTable,
    canOpenSatisfactionView: isSatisfactionTable,
    canOpenParameterCreate: isParameterTable,
    canOpenParameterEdit: isParameterTable,
    canOpenDiscountManagementCreate: isDiscountManagementTable,
    canOpenDiscountManagementEdit: isDiscountManagementTable,
    canOpenMenuDefEdit: isMenuDefsTable,
  };
}
